/*
  ヘッダーエリアでの予測検索機能
*/

(function () {
  "use strict";

  const SELECTORS = {
    predictiveSearch: ".v2-form-hd-search-predictive-search",
    headerSticky: ".v2-header-sticky",
    headerSection: "#shopify-section-v2-header",
    searchInput: 'header form[action="/search"] input[name="q"]',
    tabItem: "[data-tab-item]",
  };

  const CLASSES = {
    open: "is-open",
    modalOpen: "is-opening-modal",
    active: "is-active",
  };

  const SEARCH_TYPES = ["product", "article", "page"];
  const MAX_RESULTS_PER_TYPE = 8;
  const SCREEN_WIDTH_THRESHOLD = 50;
  const DEBOUNCE_DELAY = 200;
  const SEARCH_TEMPLATE = "fetch-data";

  const state = {
    currentActiveTab: "",
    openingScreenWidth: 0,
    abortController: null,
    outsideClickHandler: null,
    currentSearchTerm: "",
  };

  const predictiveSearchElement = document.querySelector(
    SELECTORS.predictiveSearch
  );
  if (!predictiveSearchElement) {
    return;
  }

  const debounce = (fn, delay) => {
    let timerId;
    return (...args) => {
      clearTimeout(timerId);
      timerId = setTimeout(() => fn(...args), delay);
    };
  };

  const closeModal = () => {
    if (!predictiveSearchElement.classList.contains(CLASSES.open)) return;

    predictiveSearchElement.classList.remove(CLASSES.open);
    document.body.classList.remove(CLASSES.modalOpen);
    predictiveSearchElement.innerHTML = "";
    state.currentActiveTab = "";
    state.currentSearchTerm = "";

    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }

    if (state.outsideClickHandler) {
      document.removeEventListener("click", state.outsideClickHandler);
      state.outsideClickHandler = null;
    }
  };

  const handleOutsideClick = (event) => {
    if (
      (!predictiveSearchElement.contains(event.target) &&
        event.target.name !== "q") ||
      event.target.id === "close-predictive-search"
    ) {
      closeModal();
    }
  };

  const openModal = () => {
    if (predictiveSearchElement.classList.contains(CLASSES.open)) return;

    predictiveSearchElement.classList.add(CLASSES.open);
    document.body.classList.add(CLASSES.modalOpen);
    state.openingScreenWidth = window.innerWidth;

    if (!state.outsideClickHandler) {
      state.outsideClickHandler = handleOutsideClick;
      document.addEventListener("click", state.outsideClickHandler);
    }
  };

  const updateModalLayout = () => {
    const header = document.querySelector(SELECTORS.headerSticky);
    if (!header) return;

    const headerSection = header.querySelector(SELECTORS.headerSection);
    const headerHeight = headerSection?.clientHeight ?? 0;

    const viewportHeight = window.innerHeight;
    const modalHeight = viewportHeight - headerHeight;

    predictiveSearchElement.style.setProperty("--top", `${headerHeight}px`);
    predictiveSearchElement.style.setProperty(
      "--max-height",
      `${modalHeight}px`
    );
  };

  const extractResultHtml = (container) => {
    const cards = container.querySelectorAll(
      ".search-v2-product-list .product-card-default, .search-v2-product-list .v2-article-card"
    );
    if (cards.length === 0) return "";

    const sortedCards = Array.from(cards).sort((a, b) => {
      const aPriority = a.hasAttribute("data-priority");
      const bPriority = b.hasAttribute("data-priority");
      if (aPriority === bPriority) return 0;
      return aPriority ? -1 : 1;
    });

    return sortedCards
      .slice(0, MAX_RESULTS_PER_TYPE)
      .map((card) => card.cloneNode(true).outerHTML)
      .join("");
  };

  const fetchSingleType = async (type) => {
    const url = `/search?type=${type}&q=${encodeURIComponent(
      state.currentSearchTerm
    )}&view=${SEARCH_TEMPLATE}`;
    const response = await fetch(url, {
      signal: state.abortController.signal,
    });

    const wrapper = document.createElement("div");
    wrapper.innerHTML = await response.text();

    return { type, html: extractResultHtml(wrapper) };
  };

  const renderResults = (term, results) => {
    updateModalLayout();
    predictiveSearchElement.innerHTML = buildResultsHtml(term, results);
    bindTabEvents();
  };

  const fetchSearchResults = async (term) => {
    state.abortController?.abort();
    state.abortController = new AbortController();
    state.currentSearchTerm = term;

    // 1. 製品検索を最優先で取得・表示
    const productResult = await fetchSingleType("product");

    // 2. 製品結果をすぐに表示
    renderResults(term, [productResult]);

    // 3. 残りを並列で取得
    const otherResults = await Promise.all([
      fetchSingleType("article"),
      fetchSingleType("page"),
    ]);

    // 4. すべての結果を統合して再レンダリング
    const allResults = [productResult, ...otherResults];
    return allResults;
  };

  const buildResultsHtml = (term, results) => {
    const nonEmptyResults = results.filter((result) => result.html !== "");

    const headerHtml = `
      <div class="container">
        <h2 class="v2-form-hd-search-predictive-search__title">
          「${term}」に関連する製品・情報
        </h2>
      </div>
    `;

    if (nonEmptyResults.length === 0) {
      return `
        ${headerHtml}
        <div class="container">
          <p class="v2-form-hd-search-predictive-search__no-result">
            検索結果が見つかりませんでした。別のキーワードを試してください。
          </p>
          <div class="v2-form-hd-search-predictive-search__close-modal">
            <button type="button" id="close-predictive-search" class="link">
              検索結果を閉じる
            </button>
          </div>
        </div>
      `;
    }

    const activeType =
      state.currentActiveTab &&
      nonEmptyResults.some((result) => result.type === state.currentActiveTab)
        ? state.currentActiveTab
        : nonEmptyResults[0]?.type || "";

    state.currentActiveTab = activeType;

    const renderResultContainer = (result, isActive) => `
      <div class="container${isActive ? " is-active" : ""}" data-tab-result="${
      result.type
    }">
        <div class="v2-form-hd-search-predictive-search__product-list">
          ${result.html}
        </div>
        ${
          result.type === "product"
            ? `
              <div class="v2-form-hd-search-predictive-search__link-page">
                <a href="/search?type=product&q=${encodeURIComponent(
                  term
                )}">検索結果をもっと見る</a>
              </div>
            `
            : ""
        }
        <div class="v2-form-hd-search-predictive-search__close-modal">
          <button type="button" id="close-predictive-search" class="link">
            検索結果を閉じる
          </button>
        </div>
      </div>
    `;

    const tabsHtml =
      nonEmptyResults.length > 1
        ? `
        <div class="v2-form-hd-search-predictive-search__tab container">
          ${nonEmptyResults
            .map(
              (result) => `
            <button
              type="button"
              class="v2-form-hd-search-predictive-search__tab-item link${
                result.type === activeType ? " is-active" : ""
              }"
              data-tab-item="${result.type}"
            >
              ${
                result.type === "product"
                  ? "製品"
                  : result.type === "page"
                  ? "特集・ページ"
                  : "記事・FAQ"
              }
            </button>
          `
            )
            .join("")}
        </div>
      `
        : "";

    const resultsHtml = nonEmptyResults
      .map((result) =>
        renderResultContainer(result, result.type === activeType)
      )
      .join("");

    return `${headerHtml}${tabsHtml}${resultsHtml}`;
  };

  const bindTabEvents = () => {
    const tabButtons = predictiveSearchElement.querySelectorAll(
      SELECTORS.tabItem
    );

    tabButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        const tabButton = event.currentTarget;
        const tabType = tabButton.dataset.tabItem;
        const targetResult = predictiveSearchElement.querySelector(
          `[data-tab-result="${tabType}"]`
        );

        if (!targetResult) return;

        predictiveSearchElement
          .querySelectorAll(`${SELECTORS.tabItem}.${CLASSES.active}`)
          .forEach((activeButton) =>
            activeButton.classList.remove(CLASSES.active)
          );
        predictiveSearchElement
          .querySelectorAll(`[data-tab-result].${CLASSES.active}`)
          .forEach((activeContainer) =>
            activeContainer.classList.remove(CLASSES.active)
          );

        tabButton.classList.add(CLASSES.active);
        targetResult.classList.add(CLASSES.active);
        state.currentActiveTab = tabType;
      });
    });
  };

  const handleSearchInput = async (event) => {
    if (event.key === "Enter") return;

    const term = event.target.value.trim();
    if (!term) {
      closeModal();
      return;
    }

    openModal();

    try {
      const results = await fetchSearchResults(term);
      // すべての結果を最終レンダリング
      renderResults(term, results);
    } catch (error) {
      if (error.name === "AbortError") return;
      closeModal();
    }
  };

  const initSearchInputs = () => {
    const inputs = document.querySelectorAll(SELECTORS.searchInput);
    if (inputs.length === 0) return;

    const debouncedHandler = debounce(handleSearchInput, DEBOUNCE_DELAY);
    inputs.forEach((input) => {
      input.addEventListener("input", debouncedHandler);
    });
  };

  const handleWindowResize = () => {
    if (!predictiveSearchElement.classList.contains(CLASSES.open)) return;

    if (
      state.openingScreenWidth > 0 &&
      (window.innerWidth > state.openingScreenWidth + SCREEN_WIDTH_THRESHOLD ||
        window.innerWidth < state.openingScreenWidth - SCREEN_WIDTH_THRESHOLD)
    ) {
      closeModal();
    }
  };

  document.addEventListener("DOMContentLoaded", initSearchInputs);
  window.addEventListener("resize", handleWindowResize, { passive: true });
})();
